import React from 'react';
import { useTranslation } from 'react-i18next';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import { IOfferDetails, IUser } from '../Interfaces';

interface IUserSelection {
  candidates: IOfferDetails[];
  selectedCandidateId: string | null;
  setSelectedCandidateId: (id: string) => void;
  totalCandidates: number;
  getStatusColor: (candidate: IOfferDetails) => string;
  getUserInitials: (user: IUser) => string;
  selectedCandidate: IOfferDetails | null;
  jobOpeningId: string;
  setCandidates?: (candidates: IOfferDetails[]) => void;
  addCandidatesFromPreviousRound?: (candidateIds: string[]) => void;
  limit: number;
  page: number;
  onNextPageChange?: (page: number) => void;
  onPreviousPageChange?: (page: number) => void;
}
export const UserSelection = (props: IUserSelection) => {
  const { t } = useTranslation(NS.adminPages);
  const US = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews.offer.userSelection;
  const {
    selectedCandidate,
    candidates = [], // Provide default empty array
    selectedCandidateId,
    setSelectedCandidateId,
    totalCandidates,
    getStatusColor,
    getUserInitials,
    limit,
    page,
    onNextPageChange,
    onPreviousPageChange,
  } = props;

  return (
    <section className='col-span-full bg-white bg-opacity-90 backdrop-blur-sm rounded-3xl w-full shadow-lg border border-gray-200 mb-4'>
      <div className='flex justify-between items-center border-b border-gray-200'>
        <div className='flex overflow-x-auto overflow-y-hidden gap-6 scrollbar-thin px-4 py-4 scrollbar-thumb-rounded w-full h-auto scrollbar-thumb-gray-300'>
          { candidates.map((candidate) => {
            const isActive = selectedCandidateId === candidate.jobApplicantId;
            const statusColor = getStatusColor(candidate);
            const aiScore =
              candidate.interviewApplicant?.aiOverallScore?.score || 0;
            const initials = getUserInitials(
              candidate.interviewApplicant?.user,
            );
            const user = candidate.interviewApplicant?.user || {
              firstName: '',
              lastName: '',
            };

            return (
              <div
                  key={candidate.jobApplicantId}
                  className='flex-shrink-0 flex flex-col items-center'
              >
                <div className='flex flex-col items-center relative pb-1'>
                  <button
                      onClick={() =>
                        setSelectedCandidateId(candidate.jobApplicantId)
                      }
                      className={`w-12 h-12 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200
                                    ${
                                      isActive
                                        ? 'ring-4 ring-blue-400 shadow-md scale-110'
                                        : 'hover:scale-105'
                                    }
                                  `}
                      aria-pressed={isActive}
                      title={`${user.firstName} ${user.lastName} | AI Score: ${aiScore}% | Status: ${candidate.applicant_status}`}
                  >
                    <div
                        className={`w-full h-full rounded-full flex items-center justify-center text-white font-semibold text-base select-none
                      ${statusColor} ${isActive ? 'shadow-lg' : ''}`}
                        style={{
                          backgroundImage:
                          candidate.applicant_status === 'Offer-Extended'
                            ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                            : candidate.applicant_status === 'Offer-Accepted'
                              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                              : candidate.applicant_status === 'Offer-Declined'
                                ? 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'
                                : candidate.applicant_status === 'Offer-Withheld'
                                  ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                                  : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                        }}
                    >
                      { initials }
                    </div>
                  </button>

                  { /* AI Score below avatar */ }
                  <div
                      className='text-xs font-medium mt-1 text-gray-700 cursor-help'
                      title='AI Overall Score'
                  >
                    { aiScore }%
                  </div>

                  { /* Status text with color matching */ }
                  <div
                      className={`text-[10px] mt-0.5 max-w-[70px] truncate text-center font-medium
                    ${
                      candidate.applicant_status === 'Offer-Accepted'
                        ? 'text-emerald-600'
                        : candidate.applicant_status === 'Offer-Extended'
                          ? 'text-blue-600'
                          : candidate.applicant_status === 'Offer-Declined'
                            ? 'text-orange-600'
                            : candidate.applicant_status === 'Offer-Withheld'
                              ? 'text-amber-600'
                              : 'text-gray-500'
                    }`}
                  >
                    { candidate.applicant_status &&
                    candidate.applicant_status.includes('Offer-')
                      ? candidate.applicant_status.replace('Offer-', '')
                      : candidate.applicant_status || 'Unknown' }
                  </div>
                </div>
              </div>
            );
          }) }
        </div>
      </div>

      { /* Summary stats with status legend */ }
      <div className='px-4 py-3 bg-gray-50 text-xs flex flex-wrap items-center justify-between gap-2 rounded-b-3xl'>
        <div className='flex gap-4'>
          <span className='font-medium text-gray-700'>
            { t(US.total) }: { totalCandidates }
          </span>
          <span className='font-medium text-gray-700'>
            { t(US.selected) }: { selectedCandidate ? 1 : 0 }/{ candidates.length }
          </span>
          <span className='font-medium text-gray-700'>
            { t(US.avgScore) }:{ ' ' }
            { Math.round(
              candidates.length > 0
                ? candidates.reduce(
                  (sum, c) =>
                    sum + (c.interviewApplicant?.aiOverallScore?.score || 0),
                  0,
                ) / candidates.length
                : 0,
            ) }
            %
          </span>
        </div>
        { totalCandidates > limit && (
          <div className='mt-4 flex justify-between items-center col-span-full bg-white p-4 rounded-lg shadow'>
            <button
                onClick={onPreviousPageChange ? () => onPreviousPageChange(Math.max(1, page - 1)) : undefined}
                disabled={page === 1}
                className='px-4 py-2 bg-gray-100 rounded-md disabled:opacity-50 border border-gray-300 hover:bg-gray-200'
            >
              { t(US.previous) }
            </button>
            <span>
              { t(US.page) } { page } { t(US.of) } { Math.ceil(totalCandidates / limit) }
            </span>
            <button
                onClick={onNextPageChange ? () => onNextPageChange(page + 1) : undefined}
                disabled={page * limit >= totalCandidates}
                className='px-4 py-2 bg-gray-100 rounded-md disabled:opacity-50 border border-gray-300 hover:bg-gray-200'
            >
              { t(US.next) }
            </button>
          </div>
        ) }
        { /* Status legends */ }
        <div className='flex gap-3 flex-wrap'>
          { [
            { key: 'Extended', label: t(US.status.extended) },
            { key: 'Accepted', label: t(US.status.accepted) },
            { key: 'Declined', label: t(US.status.declined) },
            { key: 'Withheld', label: t(US.status.withheld) },
          ].map(({ key: status, label: statusLabel }) => {
            const color =
              status === 'Extended'
                ? 'bg-blue-500'
                : status === 'Accepted'
                  ? 'bg-emerald-500'
                  : status === 'Declined'
                    ? 'bg-orange-500'
                    : 'bg-amber-500';

            return (
              <div key={status} className='flex items-center gap-1'>
                <div className={`w-2.5 h-2.5 rounded-full ${color}`}></div>
                <span className='text-gray-600'>{ statusLabel }</span>
              </div>
            );
          }) }
        </div>
      </div>
    </section>
  );
};
